/**
 * SuperAdminPlanes.jsx — Gestión del catálogo de planes SaaS "Stripe-level".
 */
import { useEffect, useState, useCallback } from "react";
import SuperAdminLayout from "./SuperAdminLayout";
import { fetchPlans, createPlan, updatePlan, deletePlan, initPlans } from "@/services/superadminApi";
import {
    Plus,
    RefreshCw,
    CreditCard,
    Edit3,
    Trash2,
    CheckCircle2,
    MinusCircle,
    Zap,
    Users,
    Store,
    ShoppingBag,
    Printer,
    Package,
    ArrowRight
} from 'lucide-react';
import {
    SectionHeader,
    Badge,
    Button,
    CardGlass,
    LoadingSpinner
} from "@/components/ui/PremiumComponents";
import { toast } from "sonner";

const PLAN_DEFAULTS = {
    plan_id: "",
    name: "",
    price_monthly: 29.90,
    price_yearly: 299.00,
    currency: "EUR",
    active: true,
    features: {
        qr_orders: false,
        delivery: false,
        drivers: false,
        marketing: false,
        crm: false,
        analytics: true,
        multi_branch: false,
        smart_printing: true,
    },
    limits: {
        max_branches: 1,
        max_users: 5,
        max_menu_items: 100,
        orders_per_month: 500,
    }
};

const FEATURE_LABELS = {
    qr_orders: { label: "Pedidos QR", icon: <ShoppingBag size={14} /> },
    delivery: { label: "Logística Delivery", icon: <Package size={14} /> },
    drivers: { label: "Red de Repartidores", icon: <Zap size={14} /> },
    marketing: { label: "Herramientas Marketing", icon: <Plus size={14} /> },
    crm: { label: "Inteligencia Clientes", icon: <Users size={14} /> },
    analytics: { label: "Analítica Pro", icon: <CheckCircle2 size={14} /> },
    multi_branch: { label: "Soporte Multi-sede", icon: <Store size={14} /> },
    smart_printing: { label: "Nube de Impresión", icon: <Printer size={14} /> },
};

function ModalPlan({ plan, onClose, onSuccess }) {
    const isEditing = !!plan?.plan_id;
    const [form, setForm] = useState(plan || PLAN_DEFAULTS);
    const [loading, setLoading] = useState(false);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            if (isEditing) {
                await updatePlan(form.plan_id, form);
                toast.success("Plan actualizado correctamente.");
            } else {
                await createPlan(form);
                toast.success("Nuevo plan incorporado al catálogo.");
            }
            onSuccess();
        } catch (err) {
            toast.error("Fallo al guardar la configuración del plan.");
        } finally {
            setLoading(false);
        }
    };

    const setFeature = (key, val) => setForm(f => ({
        ...f, features: { ...f.features, [key]: val }
    }));

    const setLimit = (key, val) => setForm(f => ({
        ...f, limits: { ...f.limits, [key]: parseInt(val) || 0 }
    }));

    return (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center p-6 bg-slate-950/80 backdrop-blur-xl animate-in fade-in duration-300">
            <CardGlass className="w-full max-w-2xl max-h-[90vh] overflow-y-auto !bg-slate-900 shadow-2xl border-white/10 p-0 overflow-hidden">
                <div className="p-8 border-b border-white/5 flex justify-between items-center sticky top-0 bg-slate-900 z-10">
                    <div>
                        <h2 className="text-xl font-black text-white">{isEditing ? `Gestión: ${form.name}` : "Definición de Nuevo Plan"}</h2>
                        <p className="text-xs font-bold text-gray-500 uppercase tracking-widest mt-1">Configuración comercial y operativa</p>
                    </div>
                    <button onClick={onClose} className="p-2 text-gray-500 hover:text-white transition-colors">
                        <MinusCircle size={20} />
                    </button>
                </div>

                <form onSubmit={handleSubmit} className="p-8 space-y-10">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                        <div className="space-y-4">
                            <label className="text-[10px] font-black text-gray-500 uppercase tracking-widest px-1">Nombre Comercial</label>
                            <input
                                className="w-full bg-white/5 border border-white/10 rounded-2xl p-4 text-white font-bold outline-none focus:ring-2 focus:ring-primary/20"
                                value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} required
                                placeholder="Ej: Platinum Elite"
                            />
                        </div>
                        <div className="space-y-4">
                            <label className="text-[10px] font-black text-gray-500 uppercase tracking-widest px-1">Identificador Único</label>
                            <input
                                className="w-full bg-white/5 border border-white/10 rounded-2xl p-4 text-white font-mono text-sm outline-none focus:ring-2 focus:ring-primary/20 disabled:opacity-50"
                                value={form.plan_id} onChange={e => setForm(f => ({ ...f, plan_id: e.target.value.toLowerCase().replace(/\s+/g, '_') }))}
                                placeholder="plan_premium" disabled={isEditing} required
                            />
                        </div>
                        <div className="space-y-4">
                            <label className="text-[10px] font-black text-gray-500 uppercase tracking-widest px-1">Precio Mensual (€)</label>
                            <input
                                type="number" min="0" step="0.01"
                                className="w-full bg-white/5 border border-white/10 rounded-2xl p-4 text-white font-bold outline-none focus:ring-2 focus:ring-primary/20"
                                value={form.price_monthly} onChange={e => setForm(f => ({ ...f, price_monthly: parseFloat(e.target.value) || 0 }))}
                            />
                        </div>
                        <div className="space-y-4">
                            <label className="text-[10px] font-black text-gray-500 uppercase tracking-widest px-1">Precio Anual (€)</label>
                            <input
                                type="number" min="0" step="0.01"
                                className="w-full bg-white/5 border border-white/10 rounded-2xl p-4 text-white font-bold outline-none focus:ring-2 focus:ring-primary/20"
                                value={form.price_yearly} onChange={e => setForm(f => ({ ...f, price_yearly: parseFloat(e.target.value) || 0 }))}
                            />
                        </div>
                    </div>

                    <div className="space-y-6">
                        <label className="text-[10px] font-black text-gray-500 uppercase tracking-widest px-1">Módulos de Software Incluidos</label>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                            {Object.entries(FEATURE_LABELS).map(([key, { label, icon }]) => (
                                <label key={key} className={`flex items-center gap-4 p-4 rounded-2xl border transition-all cursor-pointer ${form.features?.[key] ? 'bg-primary/10 border-primary/30 text-white' : 'bg-white/5 border-white/5 text-gray-500 hover:bg-white/10'
                                    }`}>
                                    <input type="checkbox" checked={!!form.features?.[key]} onChange={e => setFeature(key, e.target.checked)} className="hidden" />
                                    <div className={form.features?.[key] ? 'text-primary' : 'text-gray-600'}>{icon}</div>
                                    <span className="text-sm font-bold">{label}</span>
                                    {form.features?.[key] && <CheckCircle2 size={16} className="ml-auto text-primary" />}
                                </label>
                            ))}
                        </div>
                    </div>

                    <div className="space-y-6">
                        <label className="text-[10px] font-black text-gray-500 uppercase tracking-widest px-1">Límites de Cuota Operativa</label>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                            {[
                                { key: 'max_branches', label: 'Sucursales' },
                                { key: 'max_users', label: 'Miembros' },
                                { key: 'max_menu_items', label: 'Items' },
                                { key: 'orders_per_month', label: 'Pedidos/Mes' },
                            ].map(({ key, label }) => (
                                <div key={key} className="space-y-2">
                                    <label className="text-[9px] font-black text-gray-600 uppercase text-center block">{label}</label>
                                    <input
                                        type="number" min="0"
                                        className="w-full bg-slate-800 border border-white/5 rounded-xl p-3 text-white text-center font-bold text-sm outline-none focus:ring-2 focus:ring-primary/20"
                                        value={form.limits?.[key] || 0}
                                        onChange={e => setLimit(key, e.target.value)}
                                    />
                                </div>
                            ))}
                        </div>
                    </div>

                    <div className="flex items-center gap-10 pt-6">
                        <label className="flex items-center gap-3 cursor-pointer group">
                            <div className={`w-10 h-6 rounded-full relative transition-all duration-300 ${form.active ? 'bg-success' : 'bg-slate-700'}`}>
                                <input type="checkbox" className="hidden" checked={form.active} onChange={e => setForm(f => ({ ...f, active: e.target.checked }))} />
                                <div className={`absolute top-1 w-4 h-4 rounded-full bg-white transition-all duration-300 ${form.active ? 'left-5' : 'left-1'}`} />
                            </div>
                            <span className="text-xs font-black text-white uppercase tracking-tighter">Plan Publicado</span>
                        </label>
                        <div className="flex-1 flex justify-end gap-3">
                            <Button variant="secondary" onClick={onClose}>Cancelar</Button>
                            <Button onClick={handleSubmit} disabled={loading} className="px-10">
                                {loading ? "Procesando..." : isEditing ? "Actualizar Plan" : "Publicar Nuevo Plan"}
                            </Button>
                        </div>
                    </div>
                </form>
            </CardGlass>
        </div>
    );
}

export default function SuperAdminPlanes() {
    const [plans, setPlans] = useState([]);
    const [loading, setLoading] = useState(true);
    const [modal, setModal] = useState(null);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const r = await fetchPlans();
            setPlans(r.items || []);
        } catch (error) {
            toast.error("Error al sincronizar catálogo de planes.");
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { load(); }, [load]);

    const handleDelete = async (plan) => {
        if (!window.confirm(`¿BORRAR PLAN "${plan.name.toUpperCase()}"?\nEsta acción puede afectar a la facturación activa.`)) return;
        try {
            await deletePlan(plan.plan_id);
            toast.success("Plan retirado del catálogo.");
            load();
        } catch (err) {
            toast.error("No se pudo eliminar el plan. Comprueba si hay suscripciones vinculadas.");
        }
    };

    const handleAutoSeed = async () => {
        try {
            await initPlans();
            toast.success("Catálogo inicial sembrado con éxito.");
            load();
        } catch (err) {
            toast.error("Fallo al sembrar planes iniciales.");
        }
    };

    return (
        <SuperAdminLayout>
            <div className="page-fade-in max-w-7xl mx-auto">
                <SectionHeader
                    title="Estructura de Monetización"
                    subtitle="Gestión del catálogo de planes, niveles de servicio y cuotas operativas."
                    actions={
                        <div className="flex gap-4">
                            <Button variant="secondary" onClick={load} className="p-3">
                                <RefreshCw size={18} className={loading ? "animate-spin" : ""} />
                            </Button>
                            <Button onClick={() => setModal({ mode: 'create' })} className="flex items-center gap-2">
                                <Plus size={20} /> Crear Nuevo Nivel
                            </Button>
                        </div>
                    }
                />

                {loading ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                        {[1, 2, 3].map(i => (
                            <CardGlass key={i} className="p-10 flex flex-col items-center justify-center space-y-4">
                                <LoadingSpinner />
                                <span className="text-[10px] font-black text-gray-500 uppercase animate-pulse">Cargando definición de plan...</span>
                            </CardGlass>
                        ))}
                    </div>
                ) : plans.length === 0 ? (
                    <CardGlass className="p-20 text-center border-dashed border-2 border-white/5">
                        <CreditCard size={48} className="mx-auto mb-6 text-gray-700" />
                        <h3 className="text-xl font-black text-white mb-2 tracking-tight">Catálogo Vacío</h3>
                        <p className="text-sm text-gray-500 max-w-sm mx-auto mb-8">No has definido ningún plan de servicio aún. Los planes de la landing page no estarán disponibles para contratación.</p>
                        <div className="flex flex-col sm:flex-row gap-4 justify-center">
                            <Button onClick={() => setModal({ mode: 'create' })}>Definir Manualmente</Button>
                            <Button variant="secondary" onClick={handleAutoSeed}>Sembrar Planes Iniciales</Button>
                        </div>
                    </CardGlass>
                ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                        {plans.map(plan => (
                            <CardGlass key={plan.plan_id} className={`p-8 border-t-4 flex flex-col h-full ${plan.active ? 'border-t-primary' : 'border-t-gray-700 opacity-60'}`}>
                                <div className="flex justify-between items-start mb-6">
                                    <div className="p-3 bg-white/5 rounded-2xl border border-white/5 text-primary">
                                        <Package size={24} />
                                    </div>
                                    <Badge status={plan.active ? 'success' : 'danger'}>
                                        {plan.active ? 'DISPONIBLE' : 'DEPRECADO'}
                                    </Badge>
                                </div>

                                <div className="mb-8">
                                    <h3 className="text-2xl font-black text-white tracking-tighter mb-1">{plan.name}</h3>
                                    <code className="text-[10px] font-black text-gray-500 uppercase tracking-widest">{plan.plan_id}</code>
                                </div>

                                <div className="flex items-baseline gap-2 mb-8">
                                    <span className="text-4xl font-black text-white">€{plan.price_monthly}</span>
                                    <span className="text-xs font-bold text-gray-500 uppercase">/ Mes</span>
                                </div>

                                <div className="space-y-3 mb-10 flex-1">
                                    {Object.entries(plan.features || {}).filter(([, v]) => v).slice(0, 5).map(([key]) => (
                                        <div key={key} className="flex items-center gap-3 text-sm">
                                            <div className="w-1.5 h-1.5 rounded-full bg-primary shadow-[0_0_8px_var(--primary)]" />
                                            <span className="text-gray-300 font-bold">{FEATURE_LABELS[key]?.label || key}</span>
                                        </div>
                                    ))}
                                    {Object.values(plan.features || {}).filter(v => v).length > 5 && (
                                        <div className="text-[10px] font-black text-gray-600 pl-4 uppercase">+ Ver resto de módulos</div>
                                    )}
                                </div>

                                <div className="grid grid-cols-2 gap-3 mb-10 p-5 bg-white/5 rounded-3xl border border-white/5">
                                    <div className="text-center">
                                        <div className="text-lg font-black text-white">{plan.limits?.max_branches}</div>
                                        <div className="text-[9px] font-black text-gray-600 uppercase">Sedes</div>
                                    </div>
                                    <div className="text-center border-l border-white/5">
                                        <div className="text-lg font-black text-white">{plan.limits?.max_users}</div>
                                        <div className="text-[9px] font-black text-gray-600 uppercase">Staff</div>
                                    </div>
                                </div>

                                <div className="flex gap-3">
                                    <Button variant="secondary" onClick={() => setModal({ mode: 'edit', plan })} className="flex-1 gap-2">
                                        <Edit3 size={14} /> Configurar
                                    </Button>
                                    <button
                                        onClick={() => handleDelete(plan)}
                                        className="p-3 bg-danger/5 hover:bg-danger/20 border border-danger/20 rounded-2xl text-danger transition-all"
                                    >
                                        <Trash2 size={18} />
                                    </button>
                                </div>
                            </CardGlass>
                        ))}
                    </div>
                )}

                {modal && (
                    <ModalPlan
                        plan={modal.mode === 'edit' ? modal.plan : null}
                        onClose={() => setModal(null)}
                        onSuccess={() => { setModal(null); load(); }}
                    />
                )}
            </div>
        </SuperAdminLayout>
    );
}
